import { type DaySegment, SLOTS_PER_DAY, formatSlotTime } from '@betreuung/shared';
import { cn } from '@/lib/utils';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';

export interface TimeBandProps {
  /** Chronological runs of one day, unassigned stretches included. */
  segments: readonly DaySegment[];
  persons: PersonLookup;
  /** 8px inside mobile month cells, 10px on desktop, 34px in the day expander. */
  height: number;
  /** Only the tall variant shows the mono start time inside each segment. */
  showStartTimes?: boolean;
  radius?: number;
  className?: string;
}

/**
 * The chronological 24-hour band from 00:00 to 24:00.
 *
 * Segment widths are proportional to their duration and unassigned stretches are
 * rendered grey rather than left transparent, which is what makes a day's shape
 * readable at 8 pixels tall. The same component serves the month cell, the
 * desktop cell and the day expander.
 */
export function TimeBand({
  segments,
  persons,
  height,
  showStartTimes,
  radius,
  className,
}: TimeBandProps) {
  return (
    <div
      className={cn('flex overflow-hidden', className)}
      style={{ height, borderRadius: radius }}
      aria-hidden
    >
      {segments.map((segment) => {
        const slots = segment.toSlotInDay - segment.fromSlotInDay;
        const person = segment.personId === null ? null : persons.get(segment.personId);
        return (
          <div
            key={segment.fromSlotInDay}
            // flex-grow proportional to duration keeps the band exact at any
            // width without computing percentages.
            style={{
              flexGrow: slots,
              flexBasis: 0,
              minWidth: 0,
              ...(person ? personVars(person.colorIndex) : {}),
            }}
            className={cn('relative', person ? 'person-bg' : 'bg-free-band')}
          >
            {showStartTimes && slots >= 4 ? (
              <span className="absolute top-[3px] left-[5px] font-mono text-band font-medium whitespace-nowrap opacity-90">
                {formatSlotTime(segment.fromSlotInDay)}
              </span>
            ) : null}
            {showStartTimes && slots >= 8 ? (
              <span className="absolute bottom-[3px] left-[5px] text-[9px] font-semibold whitespace-nowrap opacity-90">
                {person?.name ?? ''}
              </span>
            ) : null}
          </div>
        );
      })}
      {segments.length === 0 ? <div className="flex-1 bg-free-band" /> : null}
    </div>
  );
}

/** Total slots a band represents — a guard for the callers' assumptions. */
export const BAND_SLOTS = SLOTS_PER_DAY;
